import type { ReactNode } from 'react';
import type { NativePaymentMethod } from '../../types/nativeSales';
import { Icon, type IconName } from '../ui/Icon';
import { IconButton } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';

// Rediseño · Bloque D. Piezas pequeñas que comparten las tres pantallas del punto de venta (venta,
// cobro y venta completada). Solo dibujan: no saben de ventas, de cobros ni del servidor.

export type PosTone = 'success' | 'info' | 'warning' | 'error';

const TONE_ICON: Record<PosTone, IconName> = { success: 'success', info: 'info', warning: 'warning', error: 'warning' };

/**
 * Un aviso que se queda en la pantalla hasta que deja de ser cierto. No es un mensaje que se va
 * solo: lo que dice un aviso del punto de venta casi siempre es qué hacer —o qué NO hacer— con un
 * cobro, y eso hay que poder leerlo con el cliente enfrente.
 *
 * El tono no se dice solo con el color: cada uno lleva su icono, y el título va en palabras.
 * `role` lo decide quien lo usa: `alert` interrumpe, `status` espera su turno.
 */
export function PosNotice({ tone, role, title, children, actions, onDismiss }: {
  tone: PosTone;
  role: 'alert' | 'status';
  title?: ReactNode;
  children?: ReactNode;
  /** Botones propios del aviso. Bajan de renglón si no caben. */
  actions?: ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <div aria-live={role === 'status' ? 'polite' : undefined} className={cx('n3-pos__notice', `n3-pos__notice--${tone}`)} role={role}>
      <span aria-hidden="true" className="n3-pos__notice-icon"><Icon name={TONE_ICON[tone]} size={20} /></span>
      <div className="n3-pos__notice-body">
        {title && <p className="n3-pos__notice-title">{title}</p>}
        {children && <div className="n3-pos__notice-text">{children}</div>}
        {actions && <div className="n3-pos__notice-actions">{actions}</div>}
      </div>
      {onDismiss && (
        <IconButton className="n3-pos__notice-close" label="Ocultar aviso" onClick={onDismiss} variant="soft">
          <Icon name="close" size={18} />
        </IconButton>
      )}
    </div>
  );
}

const METHOD_ICON: Record<NativePaymentMethod, IconName> = { CASH: 'cash', CARD: 'card', TRANSFER: 'transfer', OTHER: 'more' };

/**
 * El mosaico redondo de un método de pago. Es adorno: el nombre del método va siempre al lado.
 * `large` es el del panel de un método ya elegido (60 px, como en P-027).
 */
export function PosMethodTile({ method, large = false }: { method: NativePaymentMethod; large?: boolean }) {
  return (
    <span aria-hidden="true" className={cx('n3-pos__tile', `n3-pos__tile--${method.toLowerCase()}`, large && 'n3-pos__tile--lg')}>
      <Icon name={METHOD_ICON[method]} size={large ? 32 : 26} />
    </span>
  );
}

/** El giro de una espera. Lo que se espera se dice siempre con palabras, al lado. */
export function PosSpinner({ slow = false }: { slow?: boolean }) {
  return <span aria-hidden="true" className={cx('n3-pos__spinner', slow && 'n3-pos__spinner--slow')} />;
}

/**
 * − cantidad +. Con una sola unidad, el «−» es una basura: quitar la última unidad quita el producto
 * del pedido, y eso se ve antes de tocar.
 */
export function PosStepper({ name, quantity, disabled, onDecrease, onIncrease, onRemove }: {
  name: string;
  quantity: number;
  disabled: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
  onRemove: () => void;
}) {
  const last = quantity <= 1;
  return (
    <div aria-label={`Cantidad de ${name}: ${quantity}`} className="n3-pos__stepper" role="group">
      <button
        aria-label={last ? `Quitar ${name}` : `Uno menos de ${name}`}
        className={cx('n3-pos__step', last && 'n3-pos__step--remove')}
        disabled={disabled}
        onClick={last ? onRemove : onDecrease}
        type="button"
      >
        <Icon name={last ? 'trash' : 'minus'} size={18} />
      </button>
      <span aria-hidden="true" className="n3-pos__step-qty">{quantity}</span>
      <button aria-label={`Uno más de ${name}`} className="n3-pos__step" disabled={disabled} onClick={onIncrease} type="button">
        <Icon name="plus" size={18} />
      </button>
    </div>
  );
}
